/**
 * Stamps `data-iw-hidden` on each element using resolved visibility from the browser
 * (`getComputedStyle`, `aria-hidden`) so backend LLM preprocessing can detect hidden
 * nodes without parsing CSS (cross-origin / CSS-in-JS safe).
 */

export const DATA_IW_HIDDEN = "data-iw-hidden";

const WATCHED_ATTRS = ["class", "style", "aria-hidden", "data-state"] as const;

/** Max nodes to retag per mutation target subtree (per idle flush). */
const MAX_NODES_PER_TARGET = 1000;

/**
 * Max nodes to tag per idle slice during the initial walk. Larger than the
 * mutation budget because the initial pass is a one-time cost per session and
 * we want to converge quickly without monopolising the main thread.
 */
const INITIAL_WALK_BUDGET = 2000;

function parseAriaHidden(el: Element): string | null {
	return el.getAttribute("aria-hidden");
}

/**
 * Whether the element is hidden for LLM purposes — mirrors backend `isNodeHidden`
 * style checks plus `aria-hidden="true"`.
 */
export function isElementHidden(el: Element): boolean {
	const aria = parseAriaHidden(el);
	if (aria === "true") return true;
	const cs = getComputedStyle(el);
	if (cs.display === "none") return true;
	if (cs.visibility === "hidden") return true;
	if (Number.parseFloat(cs.opacity) === 0) return true;
	return false;
}

function setHiddenAttr(el: Element): void {
	el.setAttribute(DATA_IW_HIDDEN, String(isElementHidden(el)));
}

/**
 * Walks `root` and stamps every element in the light DOM and open shadow roots.
 */
export function tagSubtree(root: Document | Element): {
	tagged: number;
	hidden: number;
} {
	let tagged = 0;
	let hidden = 0;

	const walk = (el: Element) => {
		const h = isElementHidden(el);
		el.setAttribute(DATA_IW_HIDDEN, String(h));
		tagged += 1;
		if (h) hidden += 1;
		for (const child of el.children) {
			walk(child);
		}
		const sr = el.shadowRoot;
		if (sr) {
			for (const child of sr.children) {
				if (child.nodeType === 1) walk(child as Element);
			}
		}
	};

	if (root.nodeType === 9) {
		const doc = root as Document;
		if (doc.documentElement) walk(doc.documentElement);
	} else {
		walk(root as Element);
	}

	return { tagged, hidden };
}

/**
 * Schedule an initial tagging walk of `root` in idle slices. Returns a cancel
 * function that aborts any pending slice.
 *
 * Unlike `tagSubtree` this does not block the main thread, so it is safe to
 * run *after* `rrweb.record()` starts. The resulting `data-iw-hidden`
 * attribute writes are observed by rrweb's own MutationObserver and arrive
 * downstream as ordinary attribute mutations — the backend `resolve-class-
 * visibility` preprocess fast path (with the SDK-takeover style-clear
 * behaviour) handles the staggered arrival cleanly.
 */
export function tagSubtreeIdle(
	root: Document | Element,
	onComplete?: (stats: { tagged: number; hidden: number }) => void,
): () => void {
	const stack: Element[] = [];
	const startEl =
		root.nodeType === 9
			? (root as Document).documentElement
			: (root as Element);
	if (startEl) stack.push(startEl);

	let tagged = 0;
	let hidden = 0;
	let cancelled = false;
	let idleHandle: number | undefined;
	let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

	const flushSlice = () => {
		idleHandle = undefined;
		timeoutHandle = undefined;
		if (cancelled) return;
		let remaining = INITIAL_WALK_BUDGET;
		while (remaining > 0 && stack.length > 0) {
			const el = stack.pop() as Element;
			// Skip elements detached during the walk: getComputedStyle on a
			// disconnected element returns empty values (mis-tagging it as visible).
			if (!el.isConnected) continue;
			const h = isElementHidden(el);
			el.setAttribute(DATA_IW_HIDDEN, String(h));
			tagged += 1;
			if (h) hidden += 1;
			remaining -= 1;
			// Reverse-iterate so when we pop from the stack siblings come out in
			// document order — keeps the walk pattern close to the synchronous
			// `tagSubtree` implementation above.
			const children = el.children;
			for (let i = children.length - 1; i >= 0; i -= 1) {
				stack.push(children[i] as Element);
			}
			const sr = el.shadowRoot;
			if (sr) {
				const srChildren = sr.children;
				for (let i = srChildren.length - 1; i >= 0; i -= 1) {
					const child = srChildren.item(i);
					if (child?.nodeType === 1) {
						stack.push(child as Element);
					}
				}
			}
		}
		if (cancelled) return;
		if (stack.length > 0) {
			schedule();
		} else if (onComplete) {
			try {
				onComplete({ tagged, hidden });
			} catch {
				// Never let an onComplete error escape into the recorder loop.
			}
		}
	};

	const schedule = () => {
		if (cancelled || idleHandle != null || timeoutHandle != null) return;
		if (typeof requestIdleCallback !== "undefined") {
			idleHandle = requestIdleCallback(flushSlice, { timeout: 500 });
		} else {
			timeoutHandle = setTimeout(flushSlice, 0);
		}
	};

	schedule();

	return () => {
		cancelled = true;
		if (idleHandle != null && typeof cancelIdleCallback !== "undefined") {
			cancelIdleCallback(idleHandle);
			idleHandle = undefined;
		}
		if (timeoutHandle != null) {
			clearTimeout(timeoutHandle);
			timeoutHandle = undefined;
		}
	};
}

type Budget = { remaining: number };

function retagSubtreeWithBudget(root: Element, budget: Budget): void {
	const walk = (el: Element) => {
		if (budget.remaining <= 0) return;
		setHiddenAttr(el);
		budget.remaining -= 1;
		for (const child of el.children) {
			if (budget.remaining <= 0) return;
			walk(child);
		}
		const sr = el.shadowRoot;
		if (sr && budget.remaining > 0) {
			for (const child of sr.children) {
				if (child.nodeType === 1) walk(child as Element);
				if (budget.remaining <= 0) return;
			}
		}
	};
	walk(root);
}

/**
 * Re-tags elements after DOM mutations. Batches work into idle time and caps work per tick.
 */
export function installVisibilityObserver(doc: Document): () => void {
	const pendingRoots = new Set<Element>();
	let scheduled = false;

	const flush = () => {
		scheduled = false;
		for (const root of pendingRoots) {
			const budget: Budget = { remaining: MAX_NODES_PER_TARGET };
			retagSubtreeWithBudget(root, budget);
		}
		pendingRoots.clear();
	};

	const schedule = () => {
		if (scheduled) return;
		scheduled = true;
		if (typeof requestIdleCallback !== "undefined") {
			requestIdleCallback(
				() => {
					flush();
				},
				{ timeout: 500 },
			);
		} else {
			setTimeout(flush, 0);
		}
	};

	const observer = new MutationObserver((mutations) => {
		for (const m of mutations) {
			if (m.type === "attributes" && m.target.nodeType === 1) {
				pendingRoots.add(m.target as Element);
			} else if (m.type === "childList") {
				const target = m.target as Element;
				// Stylesheet content rewrites (CSS-in-JS, React re-rendering
				// `<style>{cssText}</style>`, dynamic theme switching) usually
				// arrive as childList mutations on a `<style>` element when
				// the text node is replaced wholesale. We can't tell which
				// elements the new rules affect without re-parsing CSS, so
				// retag the whole document. The budget-based walk caps cost.
				if (target.nodeType === 1 && target.tagName === "STYLE") {
					pendingRoots.add(doc.documentElement);
					continue;
				}
				for (const n of m.addedNodes) {
					if (n.nodeType === 1) pendingRoots.add(n as Element);
				}
			} else if (m.type === "characterData") {
				// Some apps mutate the existing text node directly (e.g.
				// `styleEl.firstChild.data = "…"`); that arrives as
				// characterData rather than childList. Same handling.
				const parent = m.target.parentNode as Element | null;
				if (parent && parent.nodeType === 1 && parent.tagName === "STYLE") {
					pendingRoots.add(doc.documentElement);
				}
			}
		}
		schedule();
	});

	const obsRoot = doc.documentElement;
	if (!obsRoot) {
		return () => {};
	}

	observer.observe(obsRoot, {
		subtree: true,
		childList: true,
		attributes: true,
		attributeFilter: [...WATCHED_ATTRS],
		characterData: true,
	});

	return () => {
		observer.disconnect();
	};
}
